import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { type PaintId, type SeasonView } from "@frontier/content/season";
import type { Profile } from "@frontier/protocol/contracts";
import type { ProfileSync } from "./profile-sync";
import { api } from "./session-api";
import {
  browserSeasonUnlockStorage,
  SeasonUnlockOperations,
  type SeasonUnlockIntent,
} from "./season-unlock-operations";

export function useSeasonActions(
  profile: Profile | null,
  token: RefObject<string>,
  sync: ProfileSync,
  setBusy: (value: boolean) => void,
  setNotice: (value: string) => void,
) {
  const [operations] = useState(
    () => new SeasonUnlockOperations(browserSeasonUnlockStorage()),
  );
  const [view, setView] = useState<SeasonView | null>(null);
  const currentView = useRef<SeasonView | null>(null);
  const [pending, setPending] = useState<SeasonUnlockIntent | null>(null);
  const active = useRef(profile);
  active.current = profile;
  const sessionTicket = sync.ticket();
  const sessionToken = token.current;
  const reads = useRef(0);
  const inFlight = useRef<{
    playerId: string;
    ticket: number;
    token: string;
  } | null>(null);
  const owner = useCallback(
    () =>
      active.current
        ? {
            playerId: active.current.playerId,
            ticket: sync.ticket(),
            token: token.current,
          }
        : null,
    [sync, token],
  );
  const ownsSession = useCallback(
    (context: NonNullable<ReturnType<typeof owner>>) =>
      active.current?.playerId === context.playerId &&
      sync.ticket() === context.ticket &&
      token.current === context.token,
    [sync, token],
  );
  const refreshPending = useCallback(() => {
    const player = active.current;
    if (!player) {
      setPending(null);
      return;
    }
    try {
      setPending(operations.pending(player.playerId));
    } catch (error) {
      setNotice(
        error instanceof Error
          ? error.message
          : "Не удалось восстановить покупку сезона.",
      );
    }
  }, [operations, setNotice]);
  useEffect(() => {
    setView(null);
    currentView.current = null;
    reads.current++;
    if (inFlight.current) {
      inFlight.current = null;
      setBusy(false);
    }
    setPending(null);
    refreshPending();
    window.addEventListener("storage", refreshPending);
    return () => window.removeEventListener("storage", refreshPending);
  }, [profile?.playerId, sessionTicket, sessionToken, refreshPending, setBusy]);
  const reload = useCallback(async () => {
    const context = owner();
    if (!context?.token) return;
    const sequence = ++reads.current;
    try {
      const next = await api.season(context.token);
      if (ownsSession(context) && sequence === reads.current) {
        currentView.current = next;
        setView(next);
      }
    } catch (error) {
      if (ownsSession(context) && sequence === reads.current)
        setNotice(
          error instanceof Error ? error.message : "Не удалось получить сезон.",
        );
    }
  }, [owner, ownsSession, setNotice]);
  const commit = useCallback(
    async (operation: () => Promise<Profile>) => {
      const context = owner();
      if (!context?.token || inFlight.current) return;
      inFlight.current = context;
      setBusy(true);
      try {
        const next = await operation();
        if (ownsSession(context)) {
          reads.current++;
          sync.commit(next, context.ticket);
          await reload();
        }
      } catch (error) {
        if (ownsSession(context))
          setNotice(
            error instanceof Error
              ? error.message
              : "Не удалось подтвердить награду.",
          );
      } finally {
        if (inFlight.current === context) {
          inFlight.current = null;
          if (ownsSession(context)) setBusy(false);
        }
      }
    },
    [owner, ownsSession, sync, setBusy, setNotice, reload],
  );
  const unlock = useCallback(async () => {
    const context = owner();
    if (!context?.token || inFlight.current) return;
    inFlight.current = context;
    setBusy(true);
    try {
      let intent = operations.pending(context.playerId);
      if (!intent) {
        const quote = currentView.current;
        if (
          !quote?.unlockPrice ||
          quote.premium ||
          quote.serverNow >= quote.endsAt ||
          quote.serverNow < quote.startsAt
        )
          return;
        intent = operations.begin(context.playerId, {
          seasonId: quote.id,
          priceVersion: quote.unlockPrice.version,
        });
      }
      setPending(intent);
      const outcome = await operations.execute(
        context.playerId,
        context.token,
        intent,
        () => ownsSession(context),
      );
      if (!ownsSession(context)) return;
      refreshPending();
      reads.current++;
      if (outcome.status === "confirmed") {
        setNotice("Премиум-доступ открыт.");
      } else if (outcome.status !== "superseded")
        setNotice(
          outcome.status === "rejected" && outcome.priceChanged
            ? "Цена доступа изменилась. Проверь новые условия."
            : outcome.message,
        );
      // Read canonical state after any result; premium alone does not resolve the saved request.
      await sync.refresh(context.ticket);
      await reload();
    } catch (error) {
      if (ownsSession(context)) {
        refreshPending();
        setNotice(
          error instanceof Error
            ? error.message
            : "Не удалось проверить покупку сезона.",
        );
      }
    } finally {
      if (inFlight.current === context) {
        inFlight.current = null;
        if (ownsSession(context)) setBusy(false);
      }
    }
  }, [
    owner,
    ownsSession,
    operations,
    setBusy,
    setNotice,
    refreshPending,
    sync,
    reload,
  ]);
  return {
    season: view,
    pendingSeasonUnlock: pending,
    reloadSeason: reload,
    claimSeason: (level: number) => {
      const season = currentView.current;
      if (season)
        void commit(() =>
          api.claimSeason(token.current, { seasonId: season.id, level }),
        );
    },
    unlockSeason: () => void unlock(),
    selectPaint: (paintId: PaintId) =>
      void commit(() => api.selectPaint(token.current, paintId)),
  };
}
